import { Component, Inject, OnDestroy } from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { FORMS_MODE, IAccessGroup, IAccount } from "@comunicare/comunicare-interfaces";
import { Observable, of, Subject } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil } from "rxjs/operators";

import { UserService } from "src/app/providers/user.service";

@Component({
  selector: "app-practitioner-access-groups-dialog",
  templateUrl: "./practitioner-access-groups-dialog.component.html",
  styleUrls: ["./practitioner-access-groups-dialog.component.scss"],
})
export class PractitionerAccessGroupsDialogComponent implements OnDestroy {
  /** Minimum number of characters before searching the users on the server */
  public static readonly SEARCH_MIN_LENGTH = 2;
  public static readonly SEARCH_DEBOUNCE_TIME = 300;

  public practitionerControl = new UntypedFormControl(null);
  public filteredUsers: Observable<IAccount[]>;

  public practitioner: IAccount;
  public accessGroupsIds: string[];

  /** Subject that emits when the component has been destroyed. */
  // tslint:disable-next-line: variable-name
  private onDestroy$ = new Subject<void>();

  constructor(
    private userService: UserService,
    public dialogRef: MatDialogRef<PractitionerAccessGroupsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { mode: FORMS_MODE; allAccessGroups: IAccessGroup[]; ownAccount: IAccount }
  ) {
    this.init();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public showGroups() {
    if (this.practitionerControl.value) {
      this.practitioner = this.practitionerControl.value;
      this.accessGroupsIds = this.practitioner.accessGroups;
    }
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  /**
   * Display function for the autocomplete input
   * @param user the selected user
   * @returns
   */
  public displayFn(user: IAccount): string {
    return user ? user.firstname + " " + user.name + " (" + user.mail + ")" : "";
  }

  private init() {
    this.filteredUsers = this.practitionerControl.valueChanges.pipe(
      takeUntil(this.onDestroy$),
      // when a user is selected, the value is the selected IAccount: no need to search again
      filter((value: string | IAccount) => typeof value === "string" || !value),
      map((value: string) => (value ?? "").trim()),
      debounceTime(PractitionerAccessGroupsDialogComponent.SEARCH_DEBOUNCE_TIME),
      distinctUntilChanged(),
      switchMap((search: string) =>
        search.length >= PractitionerAccessGroupsDialogComponent.SEARCH_MIN_LENGTH
          ? this.userService.search(search).pipe(catchError(() => of([] as IAccount[])))
          : of([] as IAccount[])
      )
    );
  }
}
